import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function buildQueryString(
  params: Record<string, string | string[] | number | boolean | undefined | null>,
): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach((v) => searchParams.append(key, v))
      } else {
        searchParams.append(key, String(value))
      }
    }
  }
  return searchParams.toString()
}

const idrWholeFormatter = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 })

/** Whole IDR for form inputs; empty string when zero (placeholder UX). */
export function formatIdrWhole(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return ''
  return idrWholeFormatter.format(Math.round(amount))
}

/** Digits-only parse from a formatted or raw input string. */
export function parseIdrDigits(input: string): number {
  const digits = input.replace(/\D/g, '')
  if (!digits) return 0
  const n = Number(digits)
  return Number.isFinite(n) ? n : 0
}

export function formatCurrency(val: number | undefined | null, currency = 'USD'): string {
  if (val === undefined || val === null) return '-'
  if (currency === 'USD') {
    return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (currency === 'IDR') {
    return `IDR ${Math.round(val).toLocaleString('id-ID')}`
  }
  return `${currency} ${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return '-'
  return new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
}

/** Signed CDDS timestamps for display (dd MMM yyyy HH:mm WIB). */
export function formatWibDateTime(iso: string | undefined | null): string {
  if (!iso) return '-'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  const base = new Intl.DateTimeFormat('id-ID', {
    timeZone: 'Asia/Jakarta',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date)
  return `${base} WIB`
}
